import { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { 
  getCommissionSum, 
  searchCommissions 
} from '../../../Module/Settlement/Presentation/settlementActions';
import {
  selectCommissionSumStatus,
  selectCommissionSumExtra,
  selectCommissionSearchStatus,
  selectCommissionSearchResults,
  selectCommissionSearchMeta
} from '../../../Module/Settlement/Presentation/settlementSelectors';
import { clearCommissionSum, clearCommissionSearch } from '../../../Module/Settlement/Presentation/settlementSlice';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import type { 
  CommissionSumExtra, 
  CommissionSearchItem, 
  CommissionSearchMeta 
} from '../../../Module/Settlement/Domain/entity/SettlementTypes';

export type PaymentSummary = {
  promisedAmount: number;      // 약정 후원금액 (결제실패 + 결제완료)
  failedAmount: number;        // 결제실패 금액 (음수)
  completedAmount: number;     // 결제완료 금액
  fees: number;                // 결제 수수료 (음수)
  missionFund: number;         // 세계 선교기금 (음수)
  finalDeposit: number;        // 입금액
  year: number;
  month: number | 'all';
};

export type PaymentMethod = 'cms' | 'card';
export type PaymentStatusCode = 'complete' | 'fail';

export type PaymentDetail = {
  id: string;
  name: string;
  method: PaymentMethod;
  status: PaymentStatusCode;
  email: string;
  phone: string;
  date: string;
  message: string;
  amount: number;
  fee: number;
  profileImg: string;
  projectTitle?: string;
  donationType?: string;
  paymentDescription?: string;
};

// 날짜 범위 계산 유틸리티
const getDateRange = (year: number, month: number | 'all') => {
  if (month === 'all') {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`
    };
  }
  const monthStr = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: `${year}-${monthStr}-01`,
    endDate: `${year}-${monthStr}-${lastDay}`
  };
};

// pay_type을 한글로 변환
const getPaymentMethod = (payType: string): PaymentMethod => {
  const lowerType = payType?.toLowerCase() ?? '';
  if (lowerType.includes('card') || lowerType.includes('thebill')) {
    return 'card';
  }
  return 'cms';
};

const formatDisplayDate = (rawDate?: string) => {
  if (!rawDate) return '';
  const dateObj = new Date(rawDate);
  if (Number.isNaN(dateObj.getTime())) return '';
  return `${dateObj.getFullYear()}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${String(dateObj.getDate()).padStart(2, '0')}`;
};

export const usePaymentStatus = (
  selectedYear: number,
  selectedMonth: number | 'all',
  targetProjectId?: string
) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();

  // Project 정보 가져오기
  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  
  // 전달받은 ID가 있으면 그것을, 없으면 선택된 프로젝트나 첫 번째 프로젝트 사용
    const projectId = useMemo(() => {
    if (targetProjectId) return targetProjectId;

    if (selectedProject?.project_id) {
      return selectedProject.project_id;
    }
    if (projects.length > 0 && projects[0].project_id) {
      return projects[0].project_id;
    }
    return null;
  }, [targetProjectId, selectedProject, projects]);

  const currentProject = useMemo(() => {
    if (!projectId) return undefined;
    return projects.find(p => p.project_id === projectId || p._id === projectId);
  }, [projectId, projects]);

  // Settlement API 상태 (타입 명시)
  const summaryStatus = useAppSelector(selectCommissionSumStatus);
  const summaryExtra = useAppSelector(selectCommissionSumExtra) as CommissionSumExtra | undefined;
  
  const detailsStatus = useAppSelector(selectCommissionSearchStatus);
  const detailsResults = useAppSelector(selectCommissionSearchResults) as CommissionSearchItem[] | undefined;
  const detailsMeta = useAppSelector(selectCommissionSearchMeta) as CommissionSearchMeta | undefined;

  // 합계 데이터 가져오기
  const fetchSummary = useCallback(() => {
    if (!projectId) return;

    const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);
    
    dispatch(getCommissionSum({
      limit: 100,
      params: [
        { fld: 'projectId', val: projectId, op: 'eq' },
        { fld: 'pay_date', op: 'between', val: startDate, v2: endDate }
      ],
      local_field: 'projectId',
      from: 'projects'
    }));
  }, [dispatch, projectId, selectedYear, selectedMonth]);

  // 상세 내역 가져오기
  const fetchDetails = useCallback(() => {
    if (!projectId) return;

    const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);
    
    dispatch(searchCommissions({
      params: [
        { fld: 'projectId', val: projectId, op: 'eq' },
        { fld: 'pay_date', op: 'between', val: startDate, v2: endDate }
      ],
      local_field: 'projectId',
      from: 'projects',
      sort: 'created_at:desc',
      limit: 100
    }));
  }, [dispatch, projectId, selectedYear, selectedMonth]);

  // 날짜 변경 시 데이터 새로 가져오기
  useEffect(() => {
    if (projectId) {
      fetchSummary();
      fetchDetails();
    }
  }, [projectId, selectedYear, selectedMonth, fetchSummary, fetchDetails]);

  // 정리
  useEffect(() => {
    return () => {
      dispatch(clearCommissionSum());
      dispatch(clearCommissionSearch());
    };
  }, [dispatch]);

  // API 데이터를 UI 형식으로 변환
  const summary: PaymentSummary = useMemo(() => {
    if (!summaryExtra) {
      return {
        promisedAmount: 0,
        failedAmount: 0,
        completedAmount: 0,
        fees: 0,
        missionFund: 0,
        finalDeposit: 0,
        year: selectedYear,
        month: selectedMonth
      };
    }

    // extra에서 데이터 추출
    const completedAmount = summaryExtra.sum_real_amount ?? 0;
    const fees = -(summaryExtra.sum_commission ?? 0);
    const missionFund = -(summaryExtra.sum_manage_commission ?? 0);
    
    // 약정 금액 = 완료금액 + 수수료 + 선교기금
    const promisedAmount = completedAmount + Math.abs(fees) + Math.abs(missionFund);
    // 실패금액은 현재 API에서 직접 제공되지 않으므로 0으로
    const failedAmount = 0;
    const finalDeposit = summaryExtra.sum_real_amount ?? 0;

    return {
      promisedAmount,
      failedAmount,
      completedAmount,
      fees,
      missionFund,
      finalDeposit,
      year: selectedYear,
      month: selectedMonth
    };
  }, [summaryExtra, selectedYear, selectedMonth]);

  // 상세 내역 변환
  const details: PaymentDetail[] = useMemo(() => {
    if (!detailsResults || detailsResults.length === 0) {
      return [];
    }

    return detailsResults.map((item, index) => {
      // 프로젝트 타이틀 추출
      const projectTitle = item.subm?.[0]?.title?.replace(/<br\s*\/?>/gi, ' ') ?? '';
      
      // 시작일(sponsor_start_*)이 있으면 우선 사용, 없으면 pay_date 사용
      const formattedDate = formatDisplayDate(item.sponsor_start_date) || formatDisplayDate(item.pay_date);

      const isOneTime = ['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes((currentProject?.type ?? '').toUpperCase());
      const donationType = isOneTime ? t('page.newProject.review.fundingType.once') : t('page.newProject.review.fundingType.regular');
      const paymentDescription = item.pay_result_comment || item.result_msg || item.comment || '';

      return {
        id: item.mgmt_commission_id ?? `item-${index}`,
        name: item.sponsor_name ?? t('common.labels.anonymous'),
        method: getPaymentMethod(item.pay_type),
        status: item.real_amount > 0 ? 'complete' : 'fail',
        email: item.email ?? '',
        phone: item.sponsor_tel ?? '',
        date: formattedDate,
        message: item.comment ?? item.pay_result_comment ?? '',
        amount: item.sponsor_amount ?? 0,
        fee: item.commission ?? 0,
        profileImg: item.thumbnail || '',
        projectTitle,
        donationType,
        paymentDescription,
      } as PaymentDetail;
    });
  }, [detailsResults, t, currentProject]);

  // 데이터 존재 여부
  const hasData = details.length > 0 || (summaryExtra?.project_count ?? 0) > 0;

  // 로딩 상태
  const isLoading = summaryStatus === 'loading' || detailsStatus === 'searching';

  // 페이지네이션 정보
  const pagination = useMemo(() => ({
    totalElements: detailsMeta?.totalElements ?? 0,
    totalPages: detailsMeta?.totalPages ?? 0,
    currentPage: Math.floor((detailsMeta?.msearch?.offset ?? 0) / (detailsMeta?.perPage ?? 10)) + 1,
    perPage: detailsMeta?.perPage ?? 10
  }), [detailsMeta]);

  return {
    // 데이터
    summary,
    details,
    hasData,
    
    // 상태
    isLoading,
    summaryStatus,
    detailsStatus,
    
    // 페이지네이션
    pagination,
    
    // 액션
    fetchSummary,
    fetchDetails,
    
    // 프로젝트 정보
    projectId
  };
};

export default usePaymentStatus;
